import React, { Component } from "react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Edit2, Trash2, RefreshCw, X, Save, Eye, EyeOff, Shield, User, Phone } from "lucide-react";

const inp = (err) => ({
  width: "100%", background: err ? "#FFF5F5" : "#F9FAFB",
  border: `1.5px solid ${err ? "#FCA5A5" : "#D1D5DB"}`, borderRadius: 10,
  padding: "9px 12px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none",
});
const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 5 };

const ROLE_COLORS = { Admin: "#E1483A", Manager: "#7C3AED", Staff: "#2F5FE0", Accounts: "#B7791F" };

class UserModal extends Component {
  constructor(props) {
    super(props);
    const u = props.user;
    this.state = {
      form: {
        first_name: u?.first_name || "",
        last_name:  u?.last_name  || "",
        email:      u?.email      || "",
        phone:      u?.phone      || "",
        password:   "",
        role_id:    u?.role_id    || "",
        branch_id:  u?.branch_id  || "",
        is_active:  u?.is_active !== undefined ? u.is_active : 1,
      },
      showPass: false, saving: false, errors: {}, roles: props.roles || [],
    };
  }

  validate() {
    const { form } = this.state;
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Valid email required";
    if (!this.props.user && form.password.length < 6) errs.password = "Min 6 characters";
    if (!form.role_id) errs.role_id = "Select a role";
    if (form.phone && !/^\+?[\d\s\-()]{7,15}$/.test(form.phone)) errs.phone = "Invalid phone number";
    return errs;
  }

  async handleSubmit() {
    const errs = this.validate();
    if (Object.keys(errs).length) { this.setState({ errors: errs }); return; }
    this.setState({ saving: true });
    try {
      const payload = { ...this.state.form };
      if (!payload.password) delete payload.password;
      if (this.props.user) {
        await api.put(`/users/${this.props.user.id}`, payload);
        toast.success("User updated!");
      } else {
        await api.post("/users", payload);
        toast.success("User created!");
      }
      this.props.onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      this.setState({ saving: false });
    }
  }

  render() {
    const { onClose, user } = this.props;
    const { form, showPass, saving, errors, roles } = this.state;
    const set = (key, val) => this.setState(p => ({ form: { ...p.form, [key]: val }, errors: { ...p.errors, [key]: "" } }));

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ background: "#16213E", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
              <User size={16} color="#C9A84C" /> {user ? "Edit User" : "Create User"}
            </span>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}>
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {/* First / Last Name */}
            <div>
              <label style={lbl}>First Name *</label>
              <input value={form.first_name} onChange={e => set("first_name", e.target.value)} style={inp(errors.first_name)} placeholder="First name" />
              {errors.first_name && <p style={{ fontSize: 11, color: "#E1483A" }}>{errors.first_name}</p>}
            </div>
            <div>
              <label style={lbl}>Last Name</label>
              <input value={form.last_name} onChange={e => set("last_name", e.target.value)} style={inp(false)} placeholder="Last name" />
            </div>

            {/* Email */}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Email *</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} style={inp(errors.email)} placeholder="user@kts.com" />
              {errors.email && <p style={{ fontSize: 11, color: "#E1483A" }}>{errors.email}</p>}
            </div>

            {/* Phone */}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Phone Number</label>
              <div style={{ position: "relative" }}>
                <Phone size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none" }} />
                <input
                  type="tel" value={form.phone}
                  onChange={e => set("phone", e.target.value)}
                  style={{ ...inp(errors.phone), paddingLeft: 34 }}
                  placeholder="+91 98765 43210"
                />
              </div>
              {errors.phone && <p style={{ fontSize: 11, color: "#E1483A" }}>{errors.phone}</p>}
            </div>

            {/* Password */}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Password {user ? "(leave blank to keep)" : " *"}</label>
              <div style={{ position: "relative" }}>
                <input type={showPass ? "text" : "password"} value={form.password}
                  onChange={e => set("password", e.target.value)}
                  style={{ ...inp(errors.password), paddingRight: 40 }}
                  placeholder={user ? "New password (optional)" : "Min 6 characters"} />
                <button type="button" onClick={() => this.setState(p => ({ showPass: !p.showPass }))}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}>
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {errors.password && <p style={{ fontSize: 11, color: "#E1483A" }}>{errors.password}</p>}
            </div>

            {/* Role / Status */}
            <div>
              <label style={lbl}>Role *</label>
              <select value={form.role_id} onChange={e => set("role_id", e.target.value)} style={inp(errors.role_id)}>
                <option value="">Select role</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.role_name}</option>)}
              </select>
              {errors.role_id && <p style={{ fontSize: 11, color: "#E1483A" }}>{errors.role_id}</p>}
            </div>
            <div>
              <label style={lbl}>Status</label>
              <select value={form.is_active} onChange={e => this.setState(p => ({ form: { ...p.form, is_active: parseInt(e.target.value) } }))} style={inp(false)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={() => this.handleSubmit()} disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving ? "#9CA3AF" : "#16213E", fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              {saving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Save size={14} /> {user ? "Update" : "Create User"}</>}
            </button>
          </div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

class UsersPage extends Component {
  constructor(props) {
    super(props);
    this.state = { users: [], roles: [], loading: true, showModal: false, editUser: null };
    this.fetch = this.fetch.bind(this);
    this.handleSaved = this.handleSaved.bind(this);
  }
  componentDidMount() { this.fetch(); }

  async fetch() {
    this.setState({ loading: true });
    try {
      const [usersRes, rolesRes] = await Promise.all([api.get("/users"), api.get("/users/roles")]);
      this.setState({ users: usersRes.data.data || [], roles: rolesRes.data.data || [] });
    } catch { toast.error("Failed to load users"); }
    finally { this.setState({ loading: false }); }
  }

  handleSaved() { this.setState({ showModal: false, editUser: null }); this.fetch(); }

  async handleDelete(id, name) {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); this.fetch(); }
    catch (e) { toast.error(e.response?.data?.message || "Cannot delete"); }
  }

  render() {
    const { users, roles, loading, showModal, editUser } = this.state;
    const { user } = this.props;
    const isAdmin = user?.role_name === "Admin";

    if (!isAdmin) return (
      <div style={{ padding: "40px 20px", textAlign: "center", color: "#9CA3AF" }}>
        <Shield size={40} style={{ marginBottom: 14, color: "#D1D5DB" }} />
        <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>Admin Access Required</p>
        <p style={{ fontSize: 13 }}>Only administrators can manage users.</p>
      </div>
    );

    return (
      <div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#16213E", letterSpacing: "-0.02em" }}>
              User <span style={{ color: "#FF6B4A" }}>Management</span>
            </h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>{users.length} users registered</p>
          </div>
          <button onClick={() => this.setState({ showModal: true, editUser: null })}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 22px", borderRadius: 12, border: "none", background: "#FF6B4A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(255,107,74,0.28)" }}>
            <Plus size={16} /> Add User
          </button>
        </div>

        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
              <RefreshCw size={22} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} />
              <p>Loading…</p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  {["#", "Name", "Email", "Phone", "Role", "Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF", borderBottom: "1.5px solid #F3F4F6", background: "#FAFBFF" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}>
                    <td style={{ padding: "12px 16px", fontSize: 11, color: "#9CA3AF", fontFamily: "JetBrains Mono,monospace", borderBottom: "1px solid #F3F4F6" }}>#{u.id}</td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 9, background: ROLE_COLORS[u.role_name] || "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff", fontSize: 12, fontWeight: 800, fontFamily: "Sora,sans-serif" }}>
                          {(u.first_name || "?").trim().charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: "#111827", fontSize: 13 }}>{u.first_name} {u.last_name}</div>
                          {u.branch_name && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{u.branch_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", borderBottom: "1px solid #F3F4F6" }}>{u.email}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, borderBottom: "1px solid #F3F4F6" }}>
                      {u.phone
                        ? <a href={`tel:${u.phone}`} style={{ color: "#1D4ED8", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}><Phone size={11} />{u.phone}</a>
                        : <span style={{ color: "#D1D5DB" }}>—</span>}
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${ROLE_COLORS[u.role_name] || "#9CA3AF"}18`, color: ROLE_COLORS[u.role_name] || "#9CA3AF", border: `1px solid ${ROLE_COLORS[u.role_name] || "#9CA3AF"}44` }}>
                        {u.role_name}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: u.is_active ? "#E4F9EE" : "#FCEAE8", color: u.is_active ? "#0B7A4C" : "#B5382D", border: `1px solid ${u.is_active ? "#BEEED9" : "#F5CAC5"}` }}>
                        {u.is_active ? "● Active" : "○ Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => this.setState({ showModal: true, editUser: u })}
                          style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => this.handleDelete(u.id, `${u.first_name} ${u.last_name}`)} disabled={u.id === user?.id}
                          style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #F5CAC5", background: "#FCEAE8", color: "#E1483A", display: "flex", alignItems: "center", justifyContent: "center", cursor: u.id === user?.id ? "not-allowed" : "pointer", opacity: u.id === user?.id ? 0.4 : 1 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <UserModal user={editUser} roles={roles}
            onClose={() => this.setState({ showModal: false, editUser: null })}
            onSaved={this.handleSaved} />
        )}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}
export default UsersPage;